import { useMemo } from 'react';
import { Message, MessagePriority } from '../types/messages';

/**
 * Hook para lógica de urgência de mensagens
 */
export function useMessageUrgency() {
  /**
   * Ordena mensagens por urgência
   * Ordem: urgente > alta > normal > baixa
   * Dentro da mesma prioridade: requer_resposta > não lida > lida
   * Por data: mais recente primeiro
   */
  const sortByUrgency = useMemo(() => {
    const priorityOrder: Record<MessagePriority, number> = {
      urgente: 4,
      alta: 3,
      normal: 2,
      baixa: 1
    };

    return (messages: Message[]): Message[] => {
      return [...messages].sort((a, b) => {
        const aPriority = a.prioridade || 'normal';
        const bPriority = b.prioridade || 'normal';
        
        // 1. Prioridade (urgente primeiro)
        const priorityDiff = priorityOrder[bPriority] - priorityOrder[aPriority];
        if (priorityDiff !== 0) return priorityDiff;
        
        // 2. Requer resposta primeiro
        if (a.requer_resposta && !b.requer_resposta) return -1;
        if (!a.requer_resposta && b.requer_resposta) return 1;
        
        // 3. Não lidas primeiro
        if (!a.lida && b.lida) return -1;
        if (a.lida && !b.lida) return 1;
        
        // 4. Prazo de resposta expirado primeiro
        const aExpired = a.prazo_resposta && new Date(a.prazo_resposta) < new Date();
        const bExpired = b.prazo_resposta && new Date(b.prazo_resposta) < new Date();
        if (aExpired && !bExpired) return -1;
        if (!aExpired && bExpired) return 1;
        
        // 5. Mais recente primeiro
        const aDate = a.ultima_atualizacao || a.atualizada_em || a.criada_em;
        const bDate = b.ultima_atualizacao || b.atualizada_em || b.criada_em;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
    };
  }, []);

  /**
   * Determina se uma mensagem deve ser exibida automaticamente
   */
  const shouldAutoShow = useMemo(() => {
    return (message: Message): boolean => {
      // Mensagens urgentes sempre são exibidas
      if (message.prioridade === 'urgente' && !message.lida) {
        return true;
      }
      
      // Mensagens de alta prioridade não lidas
      if (message.prioridade === 'alta' && !message.lida) {
        return true;
      }
      
      // Mensagens que requerem resposta e não foram respondidas
      if (message.requer_resposta && !message.resposta_enviada) {
        return true;
      }
      
      // Mensagens com prazo de resposta expirado
      if (message.prazo_resposta && new Date(message.prazo_resposta) < new Date() && !message.resposta_enviada) {
        return true;
      }
      
      return false;
    };
  }, []);

  /**
   * Retorna indicador visual de urgência
   */
  const getUrgencyIndicator = useMemo(() => {
    return (message: Message): {
      color: string;
      badge: string | null;
      borderColor: string;
    } => {
      const priority = message.prioridade || 'normal';
      
      switch (priority) {
        case 'urgente':
          return {
            color: 'text-red-400',
            badge: 'URGENTE',
            borderColor: 'border-red-500'
          };
        case 'alta':
          return {
            color: 'text-orange-400',
            badge: 'ALTA',
            borderColor: 'border-orange-500'
          };
        case 'normal':
          return {
            color: 'text-blue-400',
            badge: null,
            borderColor: 'border-blue-500'
          };
        case 'baixa':
          return {
            color: 'text-gray-400',
            badge: null,
            borderColor: 'border-gray-500'
          };
        default:
          return {
            color: 'text-gray-400',
            badge: null,
            borderColor: 'border-gray-500'
          };
      }
    };
  }, []);

  /**
   * Verifica se o prazo de resposta expirou
   */
  const isResponseDeadlineExpired = useMemo(() => {
    return (message: Message): boolean => {
      if (!message.prazo_resposta) return false;
      return new Date(message.prazo_resposta) < new Date();
    };
  }, []);

  return {
    sortByUrgency,
    shouldAutoShow,
    getUrgencyIndicator,
    isResponseDeadlineExpired
  };
}

